import formatAttendeeName from './formatAttendeeName';
import { OTHER_ATTENDEE_REGISTRATION_ID, REQUEST_DATE_STATUS, REQUEST_STATUS } from './travelConstants';
import { defaultMemoize, createSelector } from 'reselect';
import { utcToDisplayDate } from '../redux/modules/timezones';
import { getRoommateRequestSettings, mapFromRoommateSetupSettings } from '../lib/HotelRequest/utils/HotelRequestUtil';

const MIN_SEARCH_LENGTH = 3;
const SHOW_AIRPORTS_WITH_STATE_CODE = [440, 469];

/*
 * Prints the room preferences for a particular booking preference
 */
export function printRoomPreferences(showPreferences, bookingPreferences, quantity, translate) {
  const isSingleRoomBooked = quantity === 1;

  if (!showPreferences) {
    return isSingleRoomBooked ? translate('EventWidgets_RegSummary_RoomRequested__resx') :
      translate('EventWidgets_RegSummary_RoomsRequested__resx', { number: quantity });
  }

  if (!bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmokingAndADA__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmokingAndADA__resx', { number: quantity });
  } else if (bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithSmokingAndADA__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmokingAndADA__resx', { number: quantity });
  } else if (!bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmoking__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmoking__resx', { number: quantity });
  } else if (bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
    return isSingleRoomBooked ? translate('EventWidgets_PasskeyHotelRequest_RoomWithSmoking__resx') :
      translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmoking__resx', { number: quantity });
  }
  return null;
}

export function makeAttendeeRegistrationIdForOther(primaryEventRegistrationId) {
  return `${OTHER_ATTENDEE_REGISTRATION_ID}~${primaryEventRegistrationId}`;
}

export function getSelectedAttendeeId(attendeeRegistrationId) {
  if (attendeeRegistrationId.startsWith(OTHER_ATTENDEE_REGISTRATION_ID)) {
    return attendeeRegistrationId.split('~').pop();
  }
  return attendeeRegistrationId;
}

/**
 * Returns primary attendee display name
 * @param {Object} nameObj
 * @param {string} nameObj.firstName
 * @param {string} nameObj.middleName
 * @param {string} nameObj.lastName
 * @param {Function} translate
 * @returns {string}
 */
function getPrimaryAttendeeDisplayName({ firstName, middleName, lastName }, translate) {
  return (!firstName && !middleName && !lastName)
    ? translate('EventWidgets_AirRequest_PrimaryNoName__resx')
    : formatAttendeeName({ firstName, middleName, lastName });
}


/**
 * make attendee options to display on ui
 * @param {Function} translate
 * @param {Object} registrations
 * @param {Object} registrations.primary primary event registration details
 * @param {Array} registrations.guests guests' registration details
 * @param {Object} settings
 * @param {boolean} settings.showGuests whether should show guest in the dropdown or not
 * @param {Object} settings.attendeeRequestCounts map of existing counts of air requests for the attendees
 * @param {number} settings.attendeeLimit per attendee air request limit
 * @param {boolean} settings.isForExisting whether this render is for an existing request or new request
 * @param {boolean} settings.isPlanner whether planner reg or not
 * @returns {Array}
 */
export function makeAttendeeOptions(
  translate,
  { primary = { attendee: { personalInformation: {} } }, guests = [] } = {},
  { showGuests = true, attendeeRequestCounts = {}, attendeeLimit, isForExisting, isPlanner }) {
  // return empty if don't need to render guests, because default selection value will be of primary
  if (!showGuests) {
    return [];
  }
  const ignoreLimit = isPlanner || isForExisting || !attendeeLimit;
  const attendeeOptionGroups = [];
  const primaryCanRequest = ignoreLimit || attendeeRequestCounts[primary.eventRegistrationId] < attendeeLimit;
  if (primaryCanRequest) {
    attendeeOptionGroups.push({
      category: translate('EventWidgets_AirRequest_Labels_Primary__resx'),
      items: [{
        name: getPrimaryAttendeeDisplayName(primary.attendee.personalInformation, translate),
        value: primary.eventRegistrationId
      }]
    });
  }
  const guestAndOtherOptions = guests
    .filter(guest => ignoreLimit || attendeeRequestCounts[guest.eventRegistrationId] < attendeeLimit)
    .map(guest => ({
      name: formatAttendeeName(guest.attendee.personalInformation),
      value: guest.eventRegistrationId
    }));
  if (primaryCanRequest) {
    guestAndOtherOptions.push({
      name: translate('EventWidgets_AirRequest_Labels_Other__resx'),
      value: makeAttendeeRegistrationIdForOther(primary.eventRegistrationId)
    });
  }
  if (guestAndOtherOptions.length) {
    attendeeOptionGroups.push({
      category: translate('EventWidgets_AirRequest_Labels_Guests__resx'),
      items: guestAndOtherOptions
    });
  }
  return attendeeOptionGroups;
}

/**
 * Checks if search text provided matches min length criteria
 * @param {string} searchText
 * @returns {boolean}
 */
export function isCorrectAirportSearchTextLength(searchText) {
  return searchText && searchText.length >= MIN_SEARCH_LENGTH;
}

/**
 * returns the formatted airport name to display on ui
 * @param {Object} airportObj
 * @param {string} airportObj.id
 * @param {string} airportObj.city
 * @param {string} airportObj.stateCode
 * @param {string} airportObj.name
 * @param {string} airportObj.code
 * @param {Function} translate
 * @returns {string}
 */
export function getAirportDisplayName({ id, city, stateCode, name, code }, translate) {
  if (SHOW_AIRPORTS_WITH_STATE_CODE.includes(id)) {
    return translate('EventWidgets_AirRequest_AirportNameWithState__resx', { city, stateCode, name, code });
  }
  return translate('EventWidgets_AirRequest_AirportName__resx', { city, name, code });
}

/**
* Returns airport sugesstions to be display on the suggestive text box
* @param {string} searchText
* @param {boolean} buildAll
* @param {Object} props
* @returns {Array}
*/
export function makeAirportSuggestions(searchText, buildAll, props) {
  if (props.isSiteEditorPreview) { // site editor can't display suggestions
    return [];
  }
  const { airports, airports: { searchResults = {} } = {}, translate } = props;
  let airportsToSuggest;
  if (buildAll) {
    airportsToSuggest = airports;
  } else {
    if (!searchText || searchText.length < MIN_SEARCH_LENGTH) {
      return [];
    }
    airportsToSuggest = searchResults[searchText];
  }
  if (airportsToSuggest) {
    return Object.keys(airportsToSuggest)
      .map(id => {
        return {
          name: getAirportDisplayName(airportsToSuggest[id], translate),
          value: id.toString(),
          sortOrder: airportsToSuggest[id].sortOrder
        };
      }).sort((one, two) => one.sortOrder - two.sortOrder);
  }
}

/**
* Returns display text for number of characters left
* @param {number} char
* @param {Function} translate
* @returns {string}
*/
export function getTextAreaCharLimitText(char, translate) {
  return char ? translate('EventWidgets_AirRequest_Charlimit__resx', { char }) :
    translate('EventWidgets_AirRequest_ZeroCharLeft__resx');
}

/**
* Converts event startDate and endDate from UTC to display time
* @param {Object} state
* @returns {Object}
*/
export const transformEventDates = createSelector(
  state => state.event,
  state => state.timezones,
  (event, timezones) => {
    const timezone = timezones[event.timezone];
    return {
      ...event,
      startDate: utcToDisplayDate(event.startDate, timezone),
      endDate: utcToDisplayDate(event.endDate, timezone),
      timezone
    };
  }
);

export const getRequestDateStatusOptions = (translate) => {
  return [
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Confirmed__resx'),
      value: REQUEST_DATE_STATUS.CONFIRMED },
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Pending__resx'),
      value: REQUEST_DATE_STATUS.PENDING },
    { name: translate('EventWidgets_PlannerFields_ShoulderDates_Denied__resx'),
      value: REQUEST_DATE_STATUS.DENIED }
  ];
};

/**
 * Returns translated request status values submitted by default.
 */
export const getRequestStatusKey = defaultMemoize((requestStatus) => {
  switch (requestStatus) {
    case REQUEST_STATUS.SUBMITTED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
    case REQUEST_STATUS.PROCESSING:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusProcessing__resx';
    case REQUEST_STATUS.APPROVED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusApproved__resx';
    case REQUEST_STATUS.RESERVED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusReserved__resx';
    case REQUEST_STATUS.DENIED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusDenied__resx';
    case REQUEST_STATUS.NOT_AVAILABLE:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusNotAvailable__resx';
    case REQUEST_STATUS.CANCELLED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusCancelled__resx';
    case REQUEST_STATUS.PENDING:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusPending__resx';
    case REQUEST_STATUS.TICKETED:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusTicketed__resx';
    default:
      return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
  }
});

/**
 * returns option object for request status listing
 */
const getRequestStatusOptionObject = defaultMemoize((translate, requestStatus) => {
  return { name: translate(getRequestStatusKey(requestStatus)),
    value: requestStatus };
});

/**
 * makes options list for request status dropdown control
 */
export const makeRequestStatusPlannerFieldOptions = defaultMemoize((translate, forAir) => {
  const options = [
    getRequestStatusOptionObject(translate, REQUEST_STATUS.SUBMITTED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.PROCESSING),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.APPROVED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.RESERVED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.DENIED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.NOT_AVAILABLE),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.CANCELLED),
    getRequestStatusOptionObject(translate, REQUEST_STATUS.PENDING)
  ];
  if (forAir) {
    options.push(getRequestStatusOptionObject(translate, REQUEST_STATUS.TICKETED));
  }
  return options;
});

/**
 * gets the currency symbol for the given currency id
 */
export const getCurrencySymbol = (currencyList = [], currencyId) => {
  const selectedCurrency = currencyList.find(currency => currency.id === currencyId);
  return (selectedCurrency ? selectedCurrency.nameOfSymbol : null);
};


/**
 * return merged roommate settings based on presence of roommateRequestSettings
 * @param configRoommateSettings
 * @param roommateRequestSettings
 * @param isForGuestSide
 * @returns {*}
 */
export const getMergedRoommateSettings = (configRoommateSettings, roommateRequestSettings, experimentSettings,
  isForGuestSide = false) => {
  const defaultRoommateSettings = getRoommateRequestSettings(experimentSettings, isForGuestSide);
  if (!roommateRequestSettings || !roommateRequestSettings.roommatePreferenceType) {
    return {
      ...defaultRoommateSettings,
      ...mapFromRoommateSetupSettings(configRoommateSettings)
    };
  }
  return null;
};
